/**
 * Report Mapping Dendrogram Component for IRP Workbook
 *
 * Visualizes how a specific report maps to the fault tree structure
 * Highlights mapped nodes with red borders and connecting lines
 * Supports centering on specific nodes and bidirectional highlighting
 * Uses D3.js for rendering
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography, useTheme} from '@mui/material';
import * as d3 from 'd3';
import {useIRPWorkbookStore} from '../../../../../../stores/irp-workbook.store';
import {FaultTreeNode} from '../../../../../../@types/faultTreeNode';
import EventSequence from '../../../../../../@types/event-sequence';
import {IrpReportNodeMappingDTO} from '../../../../../../@types/integrated-risk-picture';
import {
    ExtendedFaultTreeNode, findNodeById, toggleChildren as toggleChildrenUtil, TreeNode, wrapText
} from '../../../../../../utils/dendrogram';

interface MappingDendrogramViewProps {
    selectedReportId: number;
    nodeMappings: IrpReportNodeMappingDTO[];
    onNodeHover?: (excerpts: string[]) => void;
    centerOnNodeId?: number;
    /** The event sequence whose fault tree to render. Supplied as a prop so this view has no
     *  dependency on the IRP workbook store/provider (it is reused by the Safety Model Editor). */
    eventSequenceData?: EventSequence | null;
}

/**
 * Expands the tree to show the report's mapped nodes:
 * - Expands the branches that lead to mapped nodes, but only down to two levels past the
 *   shallowest mapped node, so it doesn't open the whole tree to base events.
 * - Collapses branches that contain no mapped nodes.
 */
const LEVELS_PAST_SHALLOWEST_MAPPED = 2;
const expandToShowMappedNodes = (root: TreeNode, mappedNodeIds: Set<number>): void => {
    // First, collapse everything
    const collapseAll = (node: TreeNode): void => {
        if (node.children) {
            node._children = node.children;
            node.children = undefined;
        }
        if (node._children) {
            node._children.forEach(child => collapseAll(child as TreeNode));
        }
    };
    collapseAll(root);

    // Shallowest depth at which any node is mapped.
    let minMappedDepth = Infinity;
    const findMinMappedDepth = (node: TreeNode): void => {
        if (mappedNodeIds.has(node.data.id)) {
            minMappedDepth = Math.min(minMappedDepth, node.depth);
        }
        const children = node._children || node.children;
        if (children) children.forEach(child => findMinMappedDepth(child as TreeNode));
    };
    findMinMappedDepth(root);
    const maxDepthToExpand = minMappedDepth + LEVELS_PAST_SHALLOWEST_MAPPED;

    // Whether a node or any of its descendants is mapped (works over collapsed _children).
    const hasMappedDescendant = (node: TreeNode): boolean => {
        if (mappedNodeIds.has(node.data.id)) return true;
        const children = node._children || node.children;
        return !!children && children.some(child => hasMappedDescendant(child as TreeNode));
    };

    // Expand nodes that lead to a mapped node, but only within the depth cap, so the mapped
    // branches open a couple of levels rather than all the way down to base events.
    const expandPathsToMapped = (node: TreeNode): void => {
        if (hasMappedDescendant(node) && node._children && node.depth < maxDepthToExpand) {
            node.children = node._children;
            node._children = undefined;
        }
        if (node.children) {
            node.children.forEach(child => expandPathsToMapped(child as TreeNode));
        }
    };
    expandPathsToMapped(root);
};

/**
 * Presentational dendrogram view. Takes the event sequence as a prop, so it has NO dependency
 * on the IRP workbook store/provider and can be reused outside the workbook (e.g. the Safety
 * Model Editor's test-mapping modal).
 */
export const MappingDendrogramView: React.FC<MappingDendrogramViewProps> = ({
    selectedReportId,
    nodeMappings,
    onNodeHover,
    centerOnNodeId,
    eventSequenceData
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const [updateTrigger, setUpdateTrigger] = useState(0); // Force re-render when nodes expand/collapse
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const rootNodeRef = useRef<TreeNode | null>(null); // Keep reference to root for updates
    const isFirstRenderRef = useRef(true); // Track if this is the first render
    const lastExpandKeyRef = useRef<string>(''); // Mapped-node signature last auto-expanded
    const currentTransformRef = useRef<d3.ZoomTransform | null>(null); // Store current zoom/pan transform

    // Process data into tree structure and calculate mapped nodes
    const {treeData, mappedNodeIds, mappingsByNodeId, justificationsByNodeId, positiveNodeIds} = useMemo(() => {
        const emptyResult = {
            treeData: null,
            mappedNodeIds: new Set<number>(),
            mappingsByNodeId: new Map<number, string[]>(),
            justificationsByNodeId: new Map<number, string[]>(),
            positiveNodeIds: new Set<number>()
        };

        // Get the initiating event's fault tree
        const initiatingEvent = eventSequenceData?.events?.[0];
        const faultTree = initiatingEvent?.faultTree;
        const faultTreeNodes = faultTree?.faultTreeNodes || [];

        if (faultTreeNodes.length === 0) {
            return emptyResult;
        }

        // Build a map of fault tree nodes by ID
        const nodeMap = new Map<number, ExtendedFaultTreeNode>();
        faultTreeNodes.forEach((node: FaultTreeNode) => {
            nodeMap.set(node.id, {
                ...node,
                children: []
            });
        });

        // Build the tree structure using childIds
        let rootNode: ExtendedFaultTreeNode | null = null;
        nodeMap.forEach((node) => {
            if (node.childIds && node.childIds.length > 0) {
                if (!node.children) {
                    node.children = [];
                }
                node.childIds.forEach((childId: number) => {
                    const childNode = nodeMap.get(childId);
                    if (childNode && node.children) {
                        node.children.push(childNode);
                    }
                });
            }

            // Find root (node without parent or parent not in map)
            if (!node.parentId || !nodeMap.has(node.parentId)) {
                rootNode = node;
            }
        });

        // If no explicit root found, use the first node
        if (!rootNode && nodeMap.size > 0) {
            rootNode = Array.from(nodeMap.values())[0];
        }

        // Build mapped node sets from the pre-filtered nodeMappings for this report
        const mappedNodes = new Set<number>();
        const positiveNodes = new Set<number>();
        const excerptsByNode = new Map<number, string[]>();
        const justificationsByNode = new Map<number, string[]>();

        nodeMappings.forEach(nodeMapping => {
            if (nodeMapping.nodeType === 'FAULT_TREE_NODE' && nodeMap.has(nodeMapping.nodeId)) {
                mappedNodes.add(nodeMapping.nodeId);

                if (nodeMapping.positiveInteraction === true) {
                    positiveNodes.add(nodeMapping.nodeId);
                }

                // Collect excerpts for highlighting
                if (nodeMapping.reportExcerpt) {
                    if (!excerptsByNode.has(nodeMapping.nodeId)) {
                        excerptsByNode.set(nodeMapping.nodeId, []);
                    }
                    excerptsByNode.get(nodeMapping.nodeId)!.push(nodeMapping.reportExcerpt);
                }

                // Collect justifications for tooltip
                if (nodeMapping.justification) {
                    if (!justificationsByNode.has(nodeMapping.nodeId)) {
                        justificationsByNode.set(nodeMapping.nodeId, []);
                    }
                    justificationsByNode.get(nodeMapping.nodeId)!.push(nodeMapping.justification);
                }
            }
        });

        return {
            treeData: rootNode,
            mappedNodeIds: mappedNodes,
            mappingsByNodeId: excerptsByNode,
            justificationsByNodeId: justificationsByNode,
            positiveNodeIds: positiveNodes
        };
    }, [nodeMappings, eventSequenceData]);

    // Handle container resize
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setDimensions({ width, height });
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Render D3 Dendrogram
    useEffect(() => {
        if (!svgRef.current || !treeData || dimensions.width === 0 || dimensions.height === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous render

        // Use container dimensions
        const width = dimensions.width;
        const height = dimensions.height;
        const margin = { top: 20, right: 120, bottom: 20, left: 80 };

        svg.attr('width', width).attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .style('width', '100%')
            .style('height', '100%');

        // Position - always left-to-right for Report Analysis
        const xOffset = margin.left;
        const g = svg.append('g')
            .attr('transform', `translate(${xOffset},${height / 2})`);

        // Toggle children on click - use shared utility
        const handleToggleChildren = (event: any, d: TreeNode): void => {
            toggleChildrenUtil(d);
            setUpdateTrigger(prev => prev + 1); // Force re-render
        };

        // Create tree layout with fixed horizontal spacing
        const treeLayout = d3.tree<ExtendedFaultTreeNode>()
            .nodeSize([55, 350]); // [vertical spacing of 55px, horizontal spacing of 350px]

        // Create hierarchy
        const root = d3.hierarchy<ExtendedFaultTreeNode>(treeData) as TreeNode;

        // Auto-expand to the report's mapped branches on first render, when the tree
        // structure changes, OR when the set of mapped nodes changes. The latter matters
        // because the fault-tree root id is identical across reports, so a root-id check
        // alone would never re-expand when mappings load late or when switching reports.
        // A manual expand/collapse (updateTrigger only) leaves the key unchanged, so the
        // else branch preserves the user's state.
        const expandKey = Array.from(mappedNodeIds).sort((a, b) => a - b).join(',');
        if (
            isFirstRenderRef.current ||
            !rootNodeRef.current ||
            rootNodeRef.current.data.id !== root.data.id ||
            lastExpandKeyRef.current !== expandKey
        ) {
            // Use intelligent expansion to show mapped nodes
            expandToShowMappedNodes(root, mappedNodeIds);
            rootNodeRef.current = root;
            lastExpandKeyRef.current = expandKey;
            isFirstRenderRef.current = false;
        } else {
            // Use the stored root to maintain expand/collapse state
            root.eachBefore((node) => {
                const n = node as TreeNode;
                const storedNode = findNodeById(rootNodeRef.current!, n.data.id);
                if (storedNode && storedNode._children) {
                    n._children = n.children;
                    n.children = undefined;
                }
            });
            rootNodeRef.current = root;
        }

        const treeNodes = treeLayout(root);

        // Always left-to-right for Report Analysis
        const xPos = (d: any): number => d.y;

        // Draw links with red styling for mapped pathways
        const links = treeNodes.links();
        g.selectAll('.link')
            .data(links)
            .enter().append('path')
            .attr('class', 'link')
            .attr('d', d3.linkHorizontal<any, any>()
                .x(d => xPos(d))
                .y(d => d.x))
            .attr('fill', 'none')
            .attr('stroke', (d: any) => {
                // Coloured line if BOTH parent and child are mapped; green if target is positive
                const sourceId = d.source.data.id;
                const targetId = d.target.data.id;
                const bothMapped = mappedNodeIds.has(sourceId) && mappedNodeIds.has(targetId);
                if (!bothMapped) return theme.palette.divider;
                return positiveNodeIds.has(targetId) ? '#2e7d32' : '#d32f2f';
            })
            .attr('stroke-width', (d: any) => {
                // Thick line (6px) if both are mapped, standard (1.5px) otherwise
                const sourceId = d.source.data.id;
                const targetId = d.target.data.id;
                const bothMapped = mappedNodeIds.has(sourceId) && mappedNodeIds.has(targetId);
                return bothMapped ? 6 : 1.5;
            });

        // Draw nodes
        const node = g.selectAll('.node')
            .data(treeNodes.descendants())
            .enter().append('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${xPos(d)},${d.x})`)
            .style('cursor', 'pointer')
            .on('click', handleToggleChildren)
            .on('mouseenter', function(event, d) {
                const nodeId = d.data.id;
                const isMapped = mappedNodeIds.has(nodeId);
                const excerpts = mappingsByNodeId.get(nodeId) || [];
                if (onNodeHover && isMapped) {
                    onNodeHover(excerpts);
                }
            })
            .on('mouseleave', function() {
                if (onNodeHover) {
                    onNodeHover([]);
                }
            });

        // Add shapes for nodes - squares for barriers, circles for precursors
        node.each(function(d) {
            const nodeClass = d.data.nodeClass;
            const nodeType = d.data.type; // Get the node type (AND, OR, etc.)
            const nodeId = d.data.id;
            const element = d3.select(this);

            // Check if this node is mapped to the selected report
            const isMapped = mappedNodeIds.has(nodeId);

            // Determine the fill color (unchanged)
            let fillColor: string;
            if (nodeClass === 'PRECURSOR') {
                fillColor = '#FFD700'; // Yellow for precursors
            } else if (nodeClass === 'BARRIER') {
                fillColor = '#4CAF50'; // Green for barriers
            } else {
                fillColor = theme.palette.grey[400]; // Grey for all other nodes
            }

            // Determine stroke color and width: green for positive, red for negative/null
            const strokeColor = isMapped
                ? (positiveNodeIds.has(nodeId) ? '#2e7d32' : '#d32f2f')
                : theme.palette.divider;
            const strokeWidth = isMapped ? 5 : 1.5;

            if (nodeClass === 'BARRIER') {
                // Draw rectangle for barriers (twice as high as wide)
                element.append('rect')
                    .attr('x', -8)  // Half of width to center
                    .attr('y', -16) // Half of height to center
                    .attr('width', 16)
                    .attr('height', 32)
                    .attr('fill', fillColor)
                    .attr('stroke', strokeColor)
                    .attr('stroke-width', strokeWidth);
            } else {
                // Draw circle for precursors and other nodes
                element.append('circle')
                    .attr('r', 12)
                    .attr('fill', fillColor)
                    .attr('stroke', strokeColor)
                    .attr('stroke-width', strokeWidth);
            }

            // Add gate symbols (AND/OR) to the right side (always LTR)
            if (nodeType === 'AND' || nodeType === 'OR') {
                const gateOffset = 25;
                const gateGroup = element.append('g')
                    .attr('transform', `translate(${gateOffset}, 0)`);

                // Background circle for the gate symbol
                gateGroup.append('circle')
                    .attr('r', 10)
                    .attr('fill', 'white')
                    .attr('stroke', theme.palette.divider)
                    .attr('stroke-width', 1);

                // Add the gate text
                gateGroup.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', '0.35em')
                    .attr('font-size', '7px')
                    .attr('font-weight', 'bold')
                    .attr('fill', theme.palette.text.primary)
                    .text(nodeType);
            }
        });

        // Add labels with text wrapping
        node.append('text')
            .attr('dy', '0.35em')
            .attr('x', d => {
                const hasChildren = !!d.children;
                const nodeType = d.data.type;
                const hasGate = nodeType === 'AND' || nodeType === 'OR';

                // Always left-to-right: children on left, leaves on right
                if (hasChildren) return -15;
                if (hasGate) return 40; // Position after the gate symbol
                return 15; // Default position
            })
            .attr('text-anchor', d => {
                const hasChildren = !!d.children;
                return hasChildren ? 'end' : 'start';
            })
            .style('font-size', '12px')
            .style('fill', theme.palette.text.primary)
            .style('pointer-events', 'none')
            .each(function(d) {
                const text = d3.select(this);
                const name = d.data.name || d.data.uniqueId;
                const maxWidth = 250; // Maximum width in pixels

                // Wrap text
                wrapText(text, name, maxWidth);
            });

        // Report count badges removed - not needed for single report view

        // Add expand/collapse indicator (plus/minus symbol) centered in the node
        node.filter(d => {
            const treeNode = d as TreeNode;
            return !!(treeNode.children || treeNode._children);
        })
            .append('text')
            .attr('x', 0)  // Center horizontally
            .attr('y', 4)  // Center vertically with slight adjustment for text baseline
            .style('text-anchor', 'middle')
            .style('font-size', '16px')
            .style('font-weight', 'bold')
            .style('fill', 'white')  // White text to contrast with node colors
            .style('pointer-events', 'none')
            .text(d => (d as TreeNode)._children ? '+' : '-');

        // Add tooltips with excerpts and justifications
        node.append('title')
            .text(d => {
                const nodeType = d.data.type || 'UNKNOWN';
                const hasChildren = (d as TreeNode).children || (d as TreeNode)._children;
                const expandHint = hasChildren ? '\n(Click to expand/collapse)' : '';
                const nodeId = d.data.id;

                // Get excerpts and justifications for this node
                const excerpts = mappingsByNodeId.get(nodeId) || [];
                const justifications = justificationsByNodeId.get(nodeId) || [];

                let mappingInfo = '';

                // The statement of truth is what the AI actually evaluated the report
                // against, so it belongs next to the justification it produced
                if (d.data.statementOfTruth) {
                    mappingInfo += `\n\nStatement of Truth:\n${d.data.statementOfTruth}`;
                }

                // Add justifications first
                if (justifications.length > 0) {
                    mappingInfo += '\n\nMapping Justification:';
                    justifications.forEach((just, idx) => {
                        mappingInfo += `\n${idx + 1}. ${just}`;
                    });
                }

                // Add excerpts
                if (excerpts.length > 0) {
                    mappingInfo += '\n\nReport Excerpt:';
                    excerpts.forEach((excerpt, idx) => {
                        mappingInfo += `\n${idx + 1}. ${excerpt}`;
                    });
                }

                // Fallback message if mapped but no data
                if (mappedNodeIds.has(nodeId) && excerpts.length === 0 && justifications.length === 0) {
                    mappingInfo += '\n\nMapped to this report (no details available)';
                }

                return `${d.data.name}\nType: ${nodeType}${mappingInfo}${expandHint}`;
            });

        // Eye icon navigation removed - not needed for Report Analysis view

        // Add zoom and pan behavior
        const zoom = d3.zoom()
            .scaleExtent([0.3, 3])
            .on('zoom', (event) => {
                g.attr('transform', event.transform);
                // Save the current transform for later use
                currentTransformRef.current = event.transform;
            });

        svg.call(zoom as any);

        // Restore previous transform if it exists, otherwise use initial positioning
        if (currentTransformRef.current) {
            // Restore the saved transform (maintains position after expand/collapse)
            svg.call(zoom.transform as any, currentTransformRef.current);
        } else {
            // Initial positioning to center the root node (first render only)
            const initialTransform = d3.zoomIdentity
                .translate(xOffset, height / 2)
                .scale(1);
            svg.call(zoom.transform as any, initialTransform);
            currentTransformRef.current = initialTransform;
        }

    }, [treeData, theme, updateTrigger, dimensions, mappedNodeIds, mappingsByNodeId, positiveNodeIds, selectedReportId]); // Re-render when dependencies change

    // Centering logic - centers on specified node when centerOnNodeId changes
    useEffect(() => {
        if (!svgRef.current || !treeData || !centerOnNodeId || dimensions.width === 0) return;

        const svg = d3.select(svgRef.current);
        const margin = { top: 20, right: 120, bottom: 20, left: 80 };
        const width = dimensions.width;
        const height = dimensions.height;

        // Create tree layout to get node positions
        const treeLayout = d3.tree<ExtendedFaultTreeNode>()
            .nodeSize([55, 350]);

        const root = d3.hierarchy<ExtendedFaultTreeNode>(treeData) as TreeNode;

        // Apply current collapse state from rootNodeRef
        if (rootNodeRef.current) {
            root.eachBefore((node) => {
                const n = node as TreeNode;
                const storedNode = findNodeById(rootNodeRef.current!, n.data.id);
                if (storedNode && storedNode._children) {
                    n._children = n.children;
                    n.children = undefined;
                }
            });
        }

        const treeNodes = treeLayout(root);

        // Find the target node
        const targetNode = treeNodes.descendants().find(d => d.data.id === centerOnNodeId);

        if (targetNode) {
            // Calculate the transform to center on this node
            const xOffset = margin.left;
            const targetX = xOffset + targetNode.y; // Node's horizontal position
            const targetY = height / 2 + targetNode.x; // Node's vertical position

            // Center the node in the viewport
            const centerX = width / 2;
            const centerY = height / 2;

            const transform = d3.zoomIdentity
                .translate(centerX - targetX, centerY - targetY)
                .scale(1); // Reset zoom to 1.0

            // Apply transform with smooth transition
            const zoom = d3.zoom()
                .scaleExtent([0.3, 3])
                .on('zoom', (event) => {
                    svg.select('g').attr('transform', event.transform);
                    currentTransformRef.current = event.transform;
                });

            svg.transition()
                .duration(750)
                .call(zoom.transform as any, transform);

            currentTransformRef.current = transform;
        }
    }, [centerOnNodeId, treeData, dimensions]);

    return (
        <Box
            ref={containerRef}
            sx={{
                flex: 1,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.grey[50],
                position: 'relative'
            }}
        >
            {treeData ? (
                <svg
                    ref={svgRef}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0
                    }}
                />
            ) : (
                <Box sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        No fault tree data available
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

/**
 * IRP workbook entry point. Reads the event sequence from the workbook store and delegates to
 * {@link MappingDendrogramView}. This preserves the original store-driven contract for all
 * existing workbook callers (they pass no eventSequenceData and rely on the store).
 */
const ReportMappingDendrogram: React.FC<Omit<MappingDendrogramViewProps, 'eventSequenceData'>> = (props) => {
    const eventSequenceData = useIRPWorkbookStore((state) => state.eventSequenceData);
    return <MappingDendrogramView {...props} eventSequenceData={eventSequenceData} />;
};

export default ReportMappingDendrogram;